// MBA Lab — MBA Program Shortlist Builder UI. Zero node_modules imports: only
// lib/shortlist-model.js (pure state + view model), lib/shortlist-criteria.js
// (the criteria/tier vocabulary), lib/school-search.js (pure ranking), and
// lib/core.js (store/analytics).
//
// This file owns everything impure: the DOM, localStorage, the memoized fetch
// of /tools/data/schools-v1.json, and analytics. It is the FIRST client
// consumer of that endpoint - the ROI calculator's prefill is resolved
// server-side - so the read side of that contract is defined here and in
// shortlist-model.js.
//
// Two rules this file must not break:
//   1. Nothing is ever written into the DOM with innerHTML. School names,
//      program names and source URLs are first-party data, but the whole page
//      is built with createElement/textContent anyway, matching every other
//      tool here.
//   2. A failed dataset fetch is LOUD. The page shows an explanation and a
//      real retry button and fires tool_data_unavailable. A shortlist tool
//      that silently renders nothing looks identical to a shortlist tool with
//      nothing in it, and the person would never know which they were seeing.

import { store, trackToolAction } from './lib/core.js';
import { hydrate, mountSyncIndicator, syncedStore } from './lib/account-sync.js';
import { CRITERIA, TIERS } from './lib/shortlist-criteria.js';
import {
  SCHEMA_VERSION,
  MAX_SCHOOLS,
  ROI_TOOL_PATH,
  createInitialState,
  parseSavedState,
  addSchool,
  removeSchool,
  setTier,
  setRating,
  setProgram,
  isFull,
  hasSchool,
  parseSchoolsParam,
  mergeUrlSchools,
  reconcileWithDataset,
  buildComparisonViewModel,
} from './lib/shortlist-model.js';
import { indexSchoolsForSearch, searchSchools, MIN_QUERY_LENGTH } from './lib/school-search.js';
import { initToolDock } from './lib/tool-dock.js';

const TOOL_NAME = 'mba-program-shortlist';
const DATASET_URL = '/tools/data/schools-v1.json';

function byId(id) {
  return document.getElementById(id);
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function clear(node) {
  if (node) node.textContent = '';
}

/** One shared, memoized fetch of the picker dataset. Memoized on the PROMISE,
 * not the resolved value, so two callers racing on first paint cannot start
 * two requests. A rejection clears the memo so the retry button can genuinely
 * try again rather than re-serving the same failed promise forever. */
function createDatasetLoader(url) {
  let promise = null;
  return {
    load() {
      if (!promise) {
        promise = fetch(url, { credentials: 'same-origin' })
          .then((res) => {
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            return res.json();
          })
          .then((data) => {
            if (!data || !Array.isArray(data.schools)) throw new Error('unexpected payload shape');
            return data;
          })
          .catch((err) => {
            promise = null;
            throw err;
          });
      }
      return promise;
    },
  };
}

class ProgramShortlist {
  constructor() {
    this.els = {
      app: byId('shortlist-app'),
      builder: byId('shortlist-builder'),
      comparison: byId('shortlist-comparison'),

      searchRoot: byId('shortlist-search'),
      searchInput: byId('shortlist-search-input'),
      searchList: byId('shortlist-search-listbox'),
      searchStatus: byId('shortlist-search-status'),
      searchClear: byId('shortlist-search-clear'),

      loading: byId('shortlist-loading'),
      dataError: byId('shortlist-data-error'),
      retry: byId('shortlist-retry'),
      reconcileNote: byId('shortlist-reconcile-note'),
      capacityNote: byId('shortlist-capacity-note'),

      selected: byId('shortlist-selected'),
      selectedEmpty: byId('shortlist-selected-empty'),

      counts: byId('shortlist-counts'),
      comparisonEmpty: byId('shortlist-comparison-empty'),
      tableWrap: byId('shortlist-table-wrap'),
      table: byId('shortlist-table'),
      tableHead: byId('shortlist-table-head'),
      tableBody: byId('shortlist-table-body'),
      cards: byId('shortlist-cards'),

      print: byId('shortlist-print'),
      startOver: byId('shortlist-start-over'),
    };

    this.dock = initToolDock({
      dock: byId('shortlist-dock'),
      jumpTarget: this.els.comparison,
      returnTarget: this.els.builder,
    });

    // Signed in: also syncs to the account (see lib/account-sync.js). Signed out, or with
    // the feature off, this is a pure passthrough to the same localStorage wrapper.
    this.shortlistStore = syncedStore(TOOL_NAME, store(TOOL_NAME), {
      schemaVersion: SCHEMA_VERSION,
      onStatus: mountSyncIndicator(),
    });
    this.loader = createDatasetLoader(DATASET_URL);
    this.dataset = null;
    this.searchIndex = [];
    this.searchOptions = [];
    this.activeIndex = -1;
    this.hasEngaged = false;

    this.state = this.loadInitialState();
    this.urlSlugs = this.readUrlSlugs();

    this.wireEvents();
    this.render();
    trackToolAction(TOOL_NAME, 'open');
    this.loadDataset();
  }

  /* ---------------------------------------------------------------- state */

  loadInitialState() {
    const { status, state } = parseSavedState(this.shortlistStore.load());
    if (status === 'ok' || status === 'migrated') return state;
    // 'malformed' and 'unsupported-newer' both start clean. Nothing is
    // overwritten until the next save, so a state written by a newer build
    // survives on disk if the user reloads on that build again.
    return createInitialState();
  }

  /** `?schools=a,b` is read here and nowhere else: client-side only, never sent
   * to the controller, and never reflected into the canonical URL (the
   * ProgramShortlist action has no VaryByQueryKeys because nothing
   * server-rendered varies by it - same arrangement as the planner's ?mode=). */
  readUrlSlugs() {
    try {
      return parseSchoolsParam(new URLSearchParams(window.location.search).get('schools'));
    } catch {
      return [];
    }
  }

  persist() {
    this.shortlistStore.save({ ...this.state, savedAt: Date.now() });
  }

  /** Every state mutation funnels through here: persist, re-render, report.
   *
   * render() rebuilds the whole editor from the view model rather than patching
   * it, which is simple and impossible to get out of step - but it also destroys
   * the element the user was standing on. Every control it builds therefore
   * carries a deterministic id, and focus is put back on the same id afterwards.
   * Without this, tabbing to a criterion chip and choosing it dumps a keyboard
   * user back at the top of the document on every single answer. */
  commit(nextState) {
    if (nextState === this.state) return;
    const focusedId = document.activeElement ? document.activeElement.id : null;
    this.state = nextState;
    this.markEngaged();
    this.persist();
    this.render();
    this.restoreFocus(focusedId);
    this.reportResult();
  }

  restoreFocus(id) {
    if (!id) return;
    const node = byId(id);
    // Null when the control genuinely no longer exists (its school was just
    // removed). The caller that did the removing moves focus somewhere sensible.
    if (node && typeof node.focus === 'function') node.focus({ preventScroll: true });
  }

  markEngaged() {
    if (this.hasEngaged) return;
    this.hasEngaged = true;
    trackToolAction(TOOL_NAME, 'engage');
  }

  /** Counts only - never a school slug, a school name, or a typed query. */
  reportResult() {
    const counts = buildComparisonViewModel(this.state, this.dataset).counts;
    // result_v2, not result: tool_result is at GA4's param-count ceiling - see analytics-events.js.
    trackToolAction(TOOL_NAME, 'result_v2', {
      school_count: counts.total,
      shortlist_count: counts.shortlist,
      maybe_count: counts.maybe,
      ruled_out_count: counts.ruledOut,
      rated_count: counts.rated,
    });
  }

  /* ---------------------------------------------------------------- data */

  loadDataset() {
    this.setDataState('loading');
    this.loader
      .load()
      .then((data) => {
        this.dataset = data;
        this.searchIndex = indexSchoolsForSearch(data.schools);
        this.applyDatasetToState();
        this.setDataState('ready');
        this.render();
        if (this.els.searchInput && document.activeElement === this.els.searchInput) {
          this.renderSearchResults(this.els.searchInput.value);
        }
      })
      .catch(() => {
        this.setDataState('error');
        // The event that makes a broken endpoint visible in analytics instead
        // of looking like a sudden drop in engagement. tool_name only.
        trackToolAction(TOOL_NAME, 'data_unavailable');
      });
  }

  /** Reconcile first (drops schools the data no longer has, with a note), then
   * merge the URL's schools - in that order, so a deep link can fill the slots
   * a reconcile just freed. */
  applyDatasetToState() {
    const { state, note } = reconcileWithDataset(this.state, this.dataset);
    this.state = state;
    this.setReconcileNote(note);

    if (this.urlSlugs.length) {
      const known = new Set(this.dataset.schools.map((s) => s.slug));
      const merged = mergeUrlSchools(this.state, this.urlSlugs, known);
      this.urlSlugs = [];
      if (merged !== this.state) {
        this.state = merged;
        this.persist();
        // A shared link that lands with schools already in it IS a result, and
        // reporting it is the only way the counts distinguish "arrived with a
        // comparison" from "built one". Deliberately no tool_engage: following
        // a link is not an interaction with this tool.
        this.reportResult();
        return;
      }
    }
    if (note) this.persist();
  }

  setReconcileNote(note) {
    const node = this.els.reconcileNote;
    if (!node) return;
    node.textContent = note || '';
    node.hidden = !note;
  }

  setDataState(status) {
    if (this.els.loading) this.els.loading.hidden = status !== 'loading';
    if (this.els.dataError) this.els.dataError.hidden = status !== 'error';
    if (this.els.searchInput) {
      this.els.searchInput.disabled = status !== 'ready';
      this.els.searchInput.setAttribute('aria-busy', status === 'loading' ? 'true' : 'false');
    }
  }

  /* -------------------------------------------------------------- search */

  closeSearchList() {
    if (!this.els.searchList) return;
    this.els.searchList.hidden = true;
    this.els.searchInput.setAttribute('aria-expanded', 'false');
    this.els.searchInput.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  setSearchActive(index) {
    if (!this.searchOptions.length) return;
    this.activeIndex = (index + this.searchOptions.length) % this.searchOptions.length;
    this.searchOptions.forEach((opt, i) => {
      opt.classList.toggle('is-active', i === this.activeIndex);
      opt.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
    });
    this.els.searchInput.setAttribute('aria-activedescendant', this.searchOptions[this.activeIndex].id);
    this.searchOptions[this.activeIndex].scrollIntoView({ block: 'nearest' });
  }

  renderSearchResults(rawValue) {
    const list = this.els.searchList;
    if (!list) return;

    if (this.els.searchClear) this.els.searchClear.hidden = rawValue.length === 0;

    if (!this.dataset || rawValue.trim().length < MIN_QUERY_LENGTH) {
      this.closeSearchList();
      if (this.els.searchStatus) this.els.searchStatus.textContent = '';
      return;
    }

    const results = searchSchools(this.searchIndex, rawValue);
    clear(list);
    this.searchOptions = [];
    this.activeIndex = -1;
    this.els.searchInput.removeAttribute('aria-activedescendant');

    if (results.length === 0) {
      const empty = el('li', 'tool-shortlist-search-empty', 'No matching school in MBA Lab’s data.');
      empty.setAttribute('role', 'presentation');
      list.appendChild(empty);
      if (this.els.searchStatus) this.els.searchStatus.textContent = 'No matching school found.';
    } else {
      results.forEach((school, i) => {
        const li = document.createElement('li');
        li.setAttribute('role', 'presentation');

        const option = el('button', 'tool-shortlist-search-opt');
        option.type = 'button';
        option.id = `shortlist-search-opt-${i}`;
        option.setAttribute('role', 'option');
        option.setAttribute('aria-selected', 'false');
        option.dataset.slug = school.slug;

        const already = hasSchool(this.state, school.slug);
        option.disabled = already || isFull(this.state);

        option.appendChild(el('span', 'tool-shortlist-search-opt-name', school.name));
        const meta = [school.city, school.stateCode].filter(Boolean).join(', ');
        option.appendChild(el('span', 'tool-shortlist-search-opt-meta', already ? `${meta} · already added` : meta));

        li.appendChild(option);
        list.appendChild(li);
        this.searchOptions.push(option);
      });
      if (this.els.searchStatus) {
        this.els.searchStatus.textContent =
          results.length === 1 ? '1 matching school.' : `${results.length} matching schools.`;
      }
    }

    list.hidden = false;
    this.els.searchInput.setAttribute('aria-expanded', 'true');
  }

  handleAdd(slug) {
    if (!slug || isFull(this.state) || hasSchool(this.state, slug)) return;
    this.commit(addSchool(this.state, slug));
    this.els.searchInput.value = '';
    this.closeSearchList();
    if (this.els.searchStatus) this.els.searchStatus.textContent = 'Added to your comparison.';
    this.els.searchInput.focus();
  }

  /* -------------------------------------------------------------- events */

  wireEvents() {
    const { searchInput, searchList, searchClear, retry, print, startOver, selected } = this.els;

    if (searchInput) {
      searchInput.addEventListener('input', () => this.renderSearchResults(searchInput.value));
      searchInput.addEventListener('focus', () => this.renderSearchResults(searchInput.value));
      searchInput.addEventListener('keydown', (e) => {
        if (e.isComposing || e.keyCode === 229) return;
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          if (searchList.hidden) this.renderSearchResults(searchInput.value);
          else this.setSearchActive(this.activeIndex + 1);
          if (!searchList.hidden && this.activeIndex === -1) this.setSearchActive(0);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (!searchList.hidden) {
            this.setSearchActive(this.activeIndex === -1 ? this.searchOptions.length - 1 : this.activeIndex - 1);
          }
        } else if (e.key === 'Enter') {
          if (!searchList.hidden && this.searchOptions.length) {
            e.preventDefault();
            const target = this.searchOptions[this.activeIndex === -1 ? 0 : this.activeIndex];
            if (!target.disabled) this.handleAdd(target.dataset.slug);
          }
        } else if (e.key === 'Escape') {
          if (!searchList.hidden) this.closeSearchList();
          else if (searchInput.value) {
            searchInput.value = '';
            this.renderSearchResults('');
          }
        } else if (e.key === 'Tab') {
          this.closeSearchList();
        }
      });
    }

    if (searchList) {
      // Keep focus in the input while an option is clicked, or blur handling
      // would tear the list down before the click lands.
      searchList.addEventListener('mousedown', (e) => {
        if (e.target.closest('.tool-shortlist-search-opt')) e.preventDefault();
      });
      searchList.addEventListener('click', (e) => {
        const option = e.target.closest('.tool-shortlist-search-opt');
        if (option && !option.disabled) this.handleAdd(option.dataset.slug);
      });
      searchList.addEventListener('pointermove', (e) => {
        const option = e.target.closest('.tool-shortlist-search-opt');
        if (!option) return;
        const i = this.searchOptions.indexOf(option);
        if (i !== -1 && i !== this.activeIndex) this.setSearchActive(i);
      });
    }

    if (searchClear) {
      searchClear.addEventListener('click', () => {
        searchInput.value = '';
        this.renderSearchResults('');
        searchInput.focus();
      });
    }

    document.addEventListener('pointerdown', (e) => {
      if (this.els.searchRoot && !this.els.searchRoot.contains(e.target)) this.closeSearchList();
    });

    if (retry) retry.addEventListener('click', () => this.loadDataset());

    // The same "Run in ROI calculator" link is rendered in three places: the per-school editor
    // cards (#shortlist-selected), the desktop table (inside #shortlist-comparison), and the
    // mobile cards (also inside #shortlist-comparison) - buildLinks() is one function, called
    // from renderSelected, renderTable, and renderCards alike. Tracking only the first container
    // would silently undercount every click-through from the actual finished comparison, which is
    // where most real ROI hand-offs happen. #shortlist-remove only ever lives in #shortlist-selected.
    const trackRoiClick = (e) => {
      const roiLink = e.target.closest('[data-shortlist-roi]');
      // The prefilled href carries a school slug, so the event sends the
      // query-less path instead - analytics never learns which school.
      if (roiLink) trackToolAction(TOOL_NAME, 'crosslink_click', { href: ROI_TOOL_PATH });
    };
    if (this.els.comparison) this.els.comparison.addEventListener('click', trackRoiClick);

    if (selected) {
      selected.addEventListener('click', (e) => {
        const removeBtn = e.target.closest('[data-shortlist-remove]');
        if (removeBtn) {
          this.commit(removeSchool(this.state, removeBtn.dataset.shortlistRemove));
          // The button that had focus is gone with its school, so send focus
          // somewhere deliberate rather than letting it fall to the document.
          if (this.els.searchInput) this.els.searchInput.focus();
          return;
        }
        trackRoiClick(e);
      });

      selected.addEventListener('change', (e) => {
        const target = e.target;
        if (target.matches('[data-shortlist-tier]')) {
          this.commit(setTier(this.state, target.dataset.shortlistTier, target.value));
        } else if (target.matches('[data-shortlist-rating]')) {
          const { shortlistRating: slug, criterion } = target.dataset;
          this.commit(setRating(this.state, slug, criterion, target.value));
        } else if (target.matches('[data-shortlist-program]')) {
          this.commit(setProgram(this.state, target.dataset.shortlistProgram, target.value || null));
        }
      });
    }

    if (print) {
      print.addEventListener('click', () => {
        trackToolAction(TOOL_NAME, 'print');
        window.print();
      });
    }

    if (startOver) {
      startOver.addEventListener('click', () => {
        this.shortlistStore.deleteAccountState();
        this.state = createInitialState();
        this.setReconcileNote(null);
        this.render();
      });
    }

    document.querySelectorAll('.content-page .dir-card.tool-card').forEach((link) => {
      link.addEventListener('click', () =>
        trackToolAction(TOOL_NAME, 'crosslink_click', { href: link.getAttribute('href') })
      );
    });
  }

  /* -------------------------------------------------------------- render */

  render() {
    // Before the fetch resolves every row is unresolvable, so the view model is
    // legitimately empty - but "you haven't added anything" is the wrong thing
    // to tell someone who has six saved schools and a slow connection. While
    // the dataset is pending (or failed, where the error panel is the
    // explanation), the empty-state copy stays out of the way.
    const pending = !this.dataset;
    const vm = buildComparisonViewModel(this.state, this.dataset);
    this.renderCapacity();
    this.renderSelected(vm, pending);
    this.renderCounts(vm);
    this.renderTable(vm);
    this.renderCards(vm);

    if (this.els.comparisonEmpty) this.els.comparisonEmpty.hidden = pending || !vm.isEmpty;
    if (this.els.tableWrap) this.els.tableWrap.hidden = vm.isEmpty;
    if (this.els.cards) this.els.cards.hidden = vm.isEmpty;

    this.dock?.setReading(this.dockReading(vm));
    this.dock?.setComplete(vm.counts.total > 0);
  }

  dockReading(vm) {
    if (!this.dataset) return 'Loading school data';
    if (vm.counts.total === 0) return 'No schools yet';
    const noun = vm.counts.total === 1 ? 'school' : 'schools';
    return `${vm.counts.total} ${noun} · ${vm.counts.shortlist} shortlisted`;
  }

  renderCapacity() {
    const note = this.els.capacityNote;
    if (!note) return;
    const remaining = MAX_SCHOOLS - this.state.schools.length;
    note.textContent =
      remaining > 0
        ? `${remaining} of ${MAX_SCHOOLS} slots left.`
        : `All ${MAX_SCHOOLS} slots are full — remove one to add another.`;
  }

  renderCounts(vm) {
    const node = this.els.counts;
    if (!node) return;
    clear(node);
    if (vm.isEmpty) return;
    for (const tier of TIERS) {
      const key = tier.key === 'ruled-out' ? 'ruledOut' : tier.key;
      node.appendChild(el('li', 'stat-pill', `${tier.label}: ${vm.counts[key]}`));
    }
  }

  /** The editing surface: one block per school with its tier, its program
   * choice, its five criteria, and its links. */
  renderSelected(vm, pending) {
    const host = this.els.selected;
    if (!host) return;
    clear(host);

    if (this.els.selectedEmpty) this.els.selectedEmpty.hidden = pending || !vm.isEmpty;
    if (vm.isEmpty) return;

    for (const row of vm.rows) {
      const card = el('div', 'tool-shortlist-edit');

      const head = el('div', 'tool-shortlist-edit-head');
      head.appendChild(el('h3', 'tool-shortlist-edit-name', row.name));
      const remove = el('button', 'btn btn-outline-secondary btn-sm tool-shortlist-remove', 'Remove');
      remove.type = 'button';
      remove.id = `shortlist-remove-${row.slug}`;
      remove.dataset.shortlistRemove = row.slug;
      remove.setAttribute('aria-label', `Remove ${row.name} from the comparison`);
      head.appendChild(remove);
      card.appendChild(head);

      card.appendChild(this.buildTierGroup(row));
      if (row.programs.length > 1) card.appendChild(this.buildProgramSelect(row));
      for (const criterion of CRITERIA) card.appendChild(this.buildCriterionGroup(row, criterion));
      card.appendChild(this.buildLinks(row));

      host.appendChild(card);
    }
  }

  buildTierGroup(row) {
    const field = el('div', 'tool-form-field');
    field.appendChild(el('span', 'tool-form-field-label-text', 'Where does it stand?'));

    const group = el('div', 'tool-form-chip-group');
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', `Tier for ${row.name}`);

    for (const tier of TIERS) {
      const label = el('label', 'tool-form-chip');
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `shortlist-tier-${row.slug}`;
      // Deterministic so commit() can put focus back on the same control after
      // the rebuild. No `for` on the label - the input is nested inside it,
      // which is also what eng/visual's touch-target check falls back to.
      input.id = `shortlist-tier-${row.slug}-${tier.key}`;
      input.value = tier.key;
      input.checked = row.tier === tier.key;
      input.dataset.shortlistTier = row.slug;
      label.appendChild(input);
      label.appendChild(document.createTextNode(` ${tier.label}`));
      group.appendChild(label);
    }
    field.appendChild(group);
    return field;
  }

  buildProgramSelect(row) {
    const field = el('div', 'tool-form-field');
    const id = `shortlist-program-${row.slug}`;
    const label = el('label', null, 'Program to compare');
    label.setAttribute('for', id);
    field.appendChild(label);

    const select = el('select', 'form-select');
    select.id = id;
    select.dataset.shortlistProgram = row.slug;

    const placeholder = el('option', null, 'Choose a program');
    placeholder.value = '';
    select.appendChild(placeholder);

    for (const program of row.programs) {
      const option = el('option', null, program.format && program.format !== program.name
        ? `${program.name} (${program.format})`
        : program.name);
      option.value = program.slug;
      option.selected = program.slug === row.selectedProgramSlug;
      select.appendChild(option);
    }
    field.appendChild(select);
    field.appendChild(
      el('p', 'tool-form-field-note', 'Tuition, format, and length below all describe the program picked here.')
    );
    return field;
  }

  buildCriterionGroup(row, criterion) {
    const field = el('div', 'tool-form-field');
    field.appendChild(el('span', 'tool-form-field-label-text', criterion.label));
    field.appendChild(el('p', 'tool-form-field-note', criterion.helpText));

    const group = el('div', 'tool-form-chip-group');
    group.setAttribute('role', 'radiogroup');
    group.setAttribute('aria-label', `${criterion.label} for ${row.name}`);

    const current = row.ratings.find((r) => r.key === criterion.key);
    for (const value of criterion.values) {
      const label = el('label', 'tool-form-chip');
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `shortlist-${criterion.key}-${row.slug}`;
      input.id = `shortlist-${criterion.key}-${row.slug}-${value.value}`;
      input.value = value.value;
      input.checked = current && current.value === value.value;
      input.dataset.shortlistRating = row.slug;
      input.dataset.criterion = criterion.key;
      label.appendChild(input);
      label.appendChild(document.createTextNode(` ${value.label}`));
      group.appendChild(label);
    }
    field.appendChild(group);
    return field;
  }

  buildLinks(row) {
    const links = el('div', 'tool-shortlist-links');

    if (row.directoryUrl) {
      const directory = el('a', 'tool-shortlist-link', 'Full profile');
      directory.href = row.directoryUrl;
      links.appendChild(directory);
    }

    const roi = el('a', 'tool-shortlist-link', 'Run in ROI calculator');
    roi.href = row.roiHref;
    roi.dataset.shortlistRoi = '';
    links.appendChild(roi);

    // Only when the dataset actually has one - never a guessed URL.
    if (row.npcUrl) {
      const npc = el('a', 'tool-shortlist-link', 'Net price calculator');
      npc.href = row.npcUrl;
      npc.rel = 'nofollow noopener';
      npc.target = '_blank';
      links.appendChild(npc);
    }

    return links;
  }

  /* ------------------------------------------------- comparison surfaces */
  /* Both of the next two methods walk vm.rows and vm.factRows in the same
     order from the same view model. Nothing is computed in either one - if a
     figure needs to change, it changes in shortlist-model.js and both
     renderings move together. */

  renderTable(vm) {
    const head = this.els.tableHead;
    const body = this.els.tableBody;
    if (!head || !body) return;
    clear(head);
    clear(body);
    if (vm.isEmpty) return;

    const headRow = document.createElement('tr');
    const corner = el('th', 'tool-shortlist-th-corner', 'School');
    corner.scope = 'col';
    headRow.appendChild(corner);
    for (const row of vm.rows) {
      const th = el('th');
      th.scope = 'col';
      th.appendChild(el('span', 'tool-shortlist-col-name', row.name));
      th.appendChild(el('span', 'tool-shortlist-col-tier', row.tierLabel));
      headRow.appendChild(th);
    }
    head.appendChild(headRow);

    for (const factRow of vm.factRows) {
      const tr = document.createElement('tr');
      const th = el('th', null, factRow.label);
      th.scope = 'row';
      tr.appendChild(th);
      for (const row of vm.rows) {
        const td = document.createElement('td');
        this.appendFact(td, row.facts.find((f) => f.key === factRow.key));
        tr.appendChild(td);
      }
      body.appendChild(tr);
    }

    for (const criterion of vm.criteria) {
      const tr = el('tr', 'tool-shortlist-rating-row');
      const th = el('th', null, criterion.label);
      th.scope = 'row';
      tr.appendChild(th);
      for (const row of vm.rows) {
        const td = document.createElement('td');
        const rating = row.ratings.find((r) => r.key === criterion.key);
        td.appendChild(
          rating && rating.valueLabel
            ? el('span', 'tool-shortlist-rating-value', rating.valueLabel)
            : el('span', 'tool-shortlist-unrated', 'Not rated')
        );
        tr.appendChild(td);
      }
      body.appendChild(tr);
    }

    const linkRow = document.createElement('tr');
    const linkTh = el('th', null, 'Go deeper');
    linkTh.scope = 'row';
    linkRow.appendChild(linkTh);
    for (const row of vm.rows) {
      const td = document.createElement('td');
      td.appendChild(this.buildLinks(row));
      linkRow.appendChild(td);
    }
    body.appendChild(linkRow);
  }

  renderCards(vm) {
    const host = this.els.cards;
    if (!host) return;
    clear(host);
    if (vm.isEmpty) return;

    for (const row of vm.rows) {
      const card = el('article', 'tool-shortlist-card');
      const head = el('div', 'tool-shortlist-card-head');
      head.appendChild(el('h3', 'tool-shortlist-card-name', row.name));
      head.appendChild(el('span', 'tool-shortlist-card-tier', row.tierLabel));
      card.appendChild(head);

      const dl = el('dl', 'tool-shortlist-card-facts');
      for (const factRow of vm.factRows) {
        dl.appendChild(el('dt', null, factRow.label));
        const dd = document.createElement('dd');
        this.appendFact(dd, row.facts.find((f) => f.key === factRow.key));
        dl.appendChild(dd);
      }
      for (const criterion of vm.criteria) {
        dl.appendChild(el('dt', null, criterion.label));
        const rating = row.ratings.find((r) => r.key === criterion.key);
        dl.appendChild(
          rating && rating.valueLabel
            ? el('dd', 'tool-shortlist-rating-value', rating.valueLabel)
            : el('dd', 'tool-shortlist-unrated', 'Not rated')
        );
      }
      card.appendChild(dl);
      card.appendChild(this.buildLinks(row));
      host.appendChild(card);
    }
  }

  /** One place that turns a fact from the view model into DOM, so the table
   * cell and the card definition can never disagree about how an estimate or
   * a missing figure is shown. */
  appendFact(container, fact) {
    if (!fact) {
      container.appendChild(el('span', 'tool-shortlist-unrated', 'Not reported'));
      return;
    }

    const value = el('span', fact.reported ? 'tool-shortlist-fact' : 'tool-shortlist-fact tool-shortlist-fact--absent', fact.text);
    container.appendChild(value);

    if (fact.estimated) {
      container.appendChild(el('span', 'tool-shortlist-estimated', fact.estimatedLabel));
    }
    if (fact.cohortText) {
      container.appendChild(el('span', 'tool-shortlist-fact-note', fact.cohortText));
    }
    if (fact.note) {
      container.appendChild(el('span', 'tool-shortlist-fact-note', fact.note));
    }
    if (fact.verifiedText) {
      const note = el('span', 'tool-shortlist-fact-note');
      if (fact.sourceUrl) {
        const link = el('a', null, `Verified ${fact.verifiedText}`);
        link.href = fact.sourceUrl;
        link.rel = 'nofollow noopener';
        link.target = '_blank';
        note.appendChild(link);
      } else {
        note.textContent = `Verified ${fact.verifiedText}`;
      }
      container.appendChild(note);
    }
  }
}

document.addEventListener('DOMContentLoaded', async () => {
  if (!byId('shortlist-app')) return;
  // Reconcile with the account BEFORE the tool reads its own storage, so it simply loads
  // whatever won. A no-op (and no request) when signed out, and bounded by its own short
  // timeout so a slow network delays startup rather than blocking it.
  await hydrate(TOOL_NAME, store(TOOL_NAME), SCHEMA_VERSION);
  new ProgramShortlist();
});
